import styled, { useTheme } from 'styled-components';
import Button from '../../../components/Button';
import WidthContainer from '../../../components/WidthContainer';
import TextInput from '../../../components/Inputs/Text';
import {
  useLoginWithEmail,
  useLoginWithOAuth,
  usePrivy,
} from '@privy-io/react-auth';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import VerificationCodeInput from '../../../components/VerificationCodeInput';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import GoogleIcon from '../../../assets/icons/google.svg?react';
import AppleIcon from '../../../assets/icons/apple.svg?react';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { Capacitor } from '@capacitor/core';

const OutContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  gap: 24px;
  position: relative;
  max-width: 500px;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const SocialLogin = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 500px;
  align-items: center;
  justify-content: center;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    max-width: 100%;
  }
`;

const BackContainer = styled.div`
  position: absolute;
  top: calc(16px + env(safe-area-inset-top));
  left: 0px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 8px;
`;

const Logo = styled.img`
  width: 200px;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 38px;
  font-weight: 600;
  line-height: 40px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.primary.primary};
`;

const Subtitle = styled.h2`
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.text.title};
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const MobileLogin = () => {
  const [status, setStatus] = useState('awaiting-email-input');
  const { sendCode, state, loginWithCode } = useLoginWithEmail();
  const { user, ready } = usePrivy();

  const otpRef = useRef(null);

  const navigate = useNavigate();

  const handleLoginWithCode = async (code) => {
    try {
      await loginWithCode({ code });
      navigate(-1);
    } catch (error) {
      toast.error('Invalid code');
      if (otpRef.current) {
        otpRef.current.clear();
      }
    }
  };

  const emailSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const theme = useTheme();

  const email = watch('email');

  const { loading: oauthLoading, initOAuth } = useLoginWithOAuth();

  const handleSendCode = async (values) => {
    await sendCode({ email: values.email });
    setStatus('awaiting-code-input');
  };

  useEffect(() => {
    if (ready && user) {
      navigate('/app/home');
    }
  }, [user, ready]);

  return (
    <WidthContainer>
      <OutContainer>
        <Container>
          {status === 'awaiting-code-input' && (
            <BackContainer
              onClick={() => {
                setStatus('awaiting-email-input');
                setValue('email', '');
              }}
            >
              <img src='/chevron-left.svg' width={16} />
            </BackContainer>
          )}

          {status === 'awaiting-email-input' && (
            <>
              <Logo src='/iconLogo.svg' />
              <ContentContainer>
                <TitleContainer>
                  <Title>Welcome to Deks</Title>
                  <Subtitle>The easiest way to gift crypto</Subtitle>
                </TitleContainer>
                <FormContainer onSubmit={handleSubmit(handleSendCode)}>
                  <Controller
                    name='email'
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        inputMode='email'
                        placeholder='your@email.com'
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Button
                    loading={state?.status === 'sending-code' || oauthLoading}
                    fullWidth={true}
                    disabled={!isValid}
                  >
                    Continue
                  </Button>
                </FormContainer>
                {!Capacitor.isNativePlatform() && (
                  <SocialLogin>
                    <div style={{ width: '100%' }}>
                      <Button
                        style={{
                          background: theme.background.primary,
                          border: `1px solid ${theme.border.primary}`,
                          color: theme.text.title,
                        }}
                        fullWidth={true}
                        loading={oauthLoading}
                        onClick={() => initOAuth({ provider: 'google' })}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <GoogleIcon />
                          Continue with Google
                        </div>
                      </Button>
                    </div>
                    <div style={{ width: '100%' }}>
                      <Button
                        style={{
                          background: theme.background.primary,
                          border: `1px solid ${theme.border.primary}`,
                          color: theme.text.title,
                        }}
                        fullWidth={true}
                        loading={oauthLoading}
                        onClick={() => initOAuth({ provider: 'apple' })}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <AppleIcon />
                          Continue with Apple
                        </div>
                      </Button>
                    </div>
                  </SocialLogin>
                )}
              </ContentContainer>
            </>
          )}
          {status === 'awaiting-code-input' && (
            <>
              <ContentContainer>
                <TitleContainer>
                  <Title>Verification code</Title>
                  <Subtitle>
                    Please check {email} for an email from privy.io and enter
                    your code below.
                  </Subtitle>
                </TitleContainer>
                <FormContainer>
                  <VerificationCodeInput
                    ref={otpRef}
                    length={6}
                    loading={state?.status === 'submitting-code'}
                    onComplete={(code) => handleLoginWithCode(code)}
                    size='md'
                    autoFocus
                  />
                </FormContainer>
              </ContentContainer>
            </>
          )}
        </Container>
      </OutContainer>
    </WidthContainer>
  );
};

export default MobileLogin;
